import {
  diaNegocio,
  ETIQUETAS_MOTIVO,
  formatearMoneda,
  inicioSemana,
  POR_PAGINA_BOVEDA,
  sumarDias,
  type Boveda,
  type GastoAnteriorVista,
  type HistorialBoveda,
  type SaldosBoveda,
} from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownToLine, ArrowLeftRight, ArrowUpFromLine, Landmark, ReceiptText } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Pestana, PestanaContenido, Pestanas, PestanasLista, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input, Select } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { avisarError } from '@/lib/avisos';
import { fecha, fechaHora } from '@/lib/utils';
import { aCentavos, ETIQUETA_BOVEDA, ETIQUETA_CATEGORIA, ETIQUETA_TIPO } from './etiquetas';

export function useSaldos(habilitado = true) {
  return useQuery({ queryKey: ['boveda', 'saldos'], queryFn: () => api<SaldosBoveda>('/boveda/saldos'), enabled: habilitado });
}

type Accion = { tipo: 'ingresar' } | { tipo: 'retirar' } | { tipo: 'traspasar' } | { tipo: 'inicial'; boveda: Boveda; moneda: 'MXN' | 'USD' };

function TarjetaSaldo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <Tarjeta className="flex-1 p-4">
      <section aria-label={titulo}>
        <p className="text-[12px] uppercase tracking-wide text-foreground-muted">{titulo}</p>
        <div className="mt-1 flex flex-col gap-0.5">{children}</div>
      </section>
    </Tarjeta>
  );
}

/** Diálogo único para ingresar, retirar, traspasar y capturar el saldo inicial. */
function DialogoBoveda({ accion, onCerrar }: { accion: Accion; onCerrar: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState({
    boveda: (accion.tipo === 'inicial' ? accion.boveda : 'efectivo') as Boveda,
    moneda: (accion.tipo === 'inicial' ? accion.moneda : 'MXN') as 'MXN' | 'USD',
    monto: '',
    motivo: '',
    concepto: '',
  });
  const titulo = {
    ingresar: 'Ingresar dinero',
    retirar: 'Retirar / pagar',
    traspasar: 'Traspasar entre bóvedas',
    inicial: accion.tipo === 'inicial' ? `Saldo inicial de ${accion.boveda} (${accion.moneda})` : '',
  }[accion.tipo];
  const monto = aCentavos(d.monto);
  const enviar = useMutation({
    mutationFn: () => {
      if (accion.tipo === 'inicial') return api('/boveda/saldo-inicial', { method: 'POST', body: { boveda: d.boveda, moneda: d.moneda, monto } });
      if (accion.tipo === 'traspasar') return api('/boveda/traspasos', { method: 'POST', body: { de: d.boveda, monto, concepto: d.concepto || undefined } });
      if (accion.tipo === 'retirar') return api('/boveda/retiros', { method: 'POST', body: { boveda: d.boveda, moneda: d.moneda, monto, motivo: d.motivo, concepto: d.concepto } });
      return api('/boveda/ingresos', { method: 'POST', body: { boveda: d.boveda, moneda: d.moneda, monto, concepto: d.concepto || undefined } });
    },
    onSuccess: () => {
      toast.success('Movimiento registrado');
      void qc.invalidateQueries({ queryKey: ['boveda'] });
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });
  const valido = (accion.tipo === 'inicial' ? d.monto !== '' && monto >= 0 : monto > 0) && (accion.tipo !== 'retirar' || (d.motivo.trim() !== '' && d.concepto.trim() !== ''));

  return (
    <Dialogo
      abierto
      ancho="sm"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={titulo}
      descripcion={accion.tipo === 'inicial' ? 'Lo que hay hoy en esta bóveda. Se captura una sola vez.' : undefined}
      pie={
        <Button onClick={() => enviar.mutate()} cargando={enviar.isPending} disabled={!valido}>
          Guardar
        </Button>
      }
    >
      <div className="grid gap-3">
        {accion.tipo !== 'inicial' && (
          <Campo etiqueta={accion.tipo === 'traspasar' ? 'Sentido' : 'Bóveda'}>
            {(p) => (
              <Select {...p} value={d.boveda} onChange={(e) => setD({ ...d, boveda: e.target.value as Boveda, moneda: e.target.value === 'banco' ? 'MXN' : d.moneda })}>
                {accion.tipo === 'traspasar' ? (
                  <>
                    <option value="efectivo">Efectivo → Banco</option>
                    <option value="banco">Banco → Efectivo</option>
                  </>
                ) : (
                  <>
                    <option value="efectivo">Efectivo (caja fuerte)</option>
                    <option value="banco">Banco</option>
                  </>
                )}
              </Select>
            )}
          </Campo>
        )}
        {accion.tipo === 'retirar' && (
          <Campo etiqueta="Motivo" requerido>
            {(p) => <Input {...p} maxLength={100} placeholder="Ej. Pago de renta, servicios, reparación" value={d.motivo} onChange={(e) => setD({ ...d, motivo: e.target.value })} />}
          </Campo>
        )}
        <div className="grid grid-cols-[1fr_100px] gap-2">
          <Campo etiqueta="Monto" requerido>
            {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={d.monto} onChange={(e) => setD({ ...d, monto: e.target.value })} className="cifras" autoFocus />}
          </Campo>
          {accion.tipo !== 'traspasar' && accion.tipo !== 'inicial' && d.boveda === 'efectivo' && (
            <Campo etiqueta="Moneda">
              {(p) => (
                <Select {...p} value={d.moneda} onChange={(e) => setD({ ...d, moneda: e.target.value as 'MXN' | 'USD' })}>
                  <option>MXN</option>
                  <option>USD</option>
                </Select>
              )}
            </Campo>
          )}
        </div>
        {accion.tipo !== 'inicial' && (
          <Campo etiqueta={accion.tipo === 'retirar' ? 'Concepto' : 'Nota (opcional)'} requerido={accion.tipo === 'retirar'}>
            {(p) => <Input {...p} maxLength={200} value={d.concepto} onChange={(e) => setD({ ...d, concepto: e.target.value })} />}
          </Campo>
        )}
        {accion.tipo === 'retirar' && <p className="text-[12px] text-foreground-muted">La nómina se paga desde Pago semanal y las compras de producto desde Inventario.</p>}
      </div>
    </Dialogo>
  );
}

function Movimientos() {
  const [filtro, setFiltro] = useState<{ boveda?: string; tipo?: string; desde?: string; hasta?: string }>({});
  const [pagina, setPagina] = useState(1);
  const query = { ...Object.fromEntries(Object.entries(filtro).filter(([, v]) => v)), pagina };
  const lista = useQuery({ queryKey: ['boveda', 'movimientos', query], queryFn: () => api<HistorialBoveda>('/boveda/movimientos', { query }) });
  const cambiar = (k: keyof typeof filtro) => (e: { target: { value: string } }) => {
    setFiltro((f) => ({ ...f, [k]: e.target.value || undefined }));
    setPagina(1);
  };
  const paginas = Math.max(1, Math.ceil((lista.data?.total ?? 0) / POR_PAGINA_BOVEDA));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <Campo etiqueta="Bóveda">
          {(p) => (
            <Select {...p} value={filtro.boveda ?? ''} onChange={cambiar('boveda')}>
              <option value="">Todas</option>
              <option value="efectivo">Efectivo</option>
              <option value="banco">Banco</option>
            </Select>
          )}
        </Campo>
        <Campo etiqueta="Tipo">
          {(p) => (
            <Select {...p} value={filtro.tipo ?? ''} onChange={cambiar('tipo')}>
              <option value="">Todos</option>
              <option value="entrada">Entradas</option>
              <option value="salida">Salidas</option>
              <option value="traspaso">Traspasos</option>
            </Select>
          )}
        </Campo>
        <Campo etiqueta="Desde">{(p) => <Input {...p} type="date" value={filtro.desde ?? ''} onChange={cambiar('desde')} />}</Campo>
        <Campo etiqueta="Hasta">{(p) => <Input {...p} type="date" value={filtro.hasta ?? ''} onChange={cambiar('hasta')} />}</Campo>
      </div>
      <Tarjeta>
        {lista.isLoading ? (
          <Esqueleto className="m-4 h-32" />
        ) : !lista.data?.items.length ? (
          <Vacio icono={<Landmark />} titulo="Sin movimientos" />
        ) : (
          <div className="overflow-x-auto">
            <Tabla>
              <thead>
                <tr>
                  <Th>Fecha</Th>
                  <Th>Bóveda</Th>
                  <Th>Tipo</Th>
                  <Th>Motivo o concepto</Th>
                  <Th>Quién</Th>
                  <Th className="text-right">Monto</Th>
                  <Th className="text-right">Saldo</Th>
                </tr>
              </thead>
              <tbody>
                {lista.data.items.map((m) => (
                  <Tr key={m.id}>
                    <Td className="cifras whitespace-nowrap">{fechaHora(m.fecha)}</Td>
                    <Td className="whitespace-nowrap">
                      {ETIQUETA_BOVEDA[m.boveda]}
                      {m.moneda === 'USD' && ' (USD)'}
                    </Td>
                    <Td className="whitespace-nowrap">
                      <Insignia tono={m.monto < 0 ? 'peligro' : 'exito'}>{ETIQUETA_TIPO[m.tipo]}</Insignia>
                    </Td>
                    <Td className="min-w-52">{[m.motivo ? ETIQUETAS_MOTIVO[m.motivo] : m.motivoLibre, m.concepto].filter(Boolean).join(' · ')}</Td>
                    <Td>{m.usuario}</Td>
                    <Td className={`cifras whitespace-nowrap text-right ${m.monto < 0 ? 'text-destructive' : 'text-success'}`}>
                      {`${m.monto < 0 ? '−' : '+'}${formatearMoneda(Math.abs(m.monto), m.moneda)}`}
                    </Td>
                    <Td className="cifras whitespace-nowrap text-right">{formatearMoneda(m.saldo, m.moneda)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Tabla>
          </div>
        )}
      </Tarjeta>
      {paginas > 1 && (
        <div className="flex items-center justify-end gap-2 text-[13px]">
          <Button variante="secundario" tamano="sm" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
            Más recientes
          </Button>
          <span className="cifras">
            {pagina} / {paginas}
          </span>
          <Button variante="secundario" tamano="sm" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>
            Anteriores
          </Button>
        </div>
      )}
    </div>
  );
}

function GastosAnteriores() {
  const hoy = diaNegocio(new Date());
  const [rango, setRango] = useState({ desde: inicioSemana(hoy, 1), hasta: sumarDias(inicioSemana(hoy, 1), 6) });
  const lista = useQuery({ queryKey: ['boveda', 'gastos-anteriores', rango], queryFn: () => api<GastoAnteriorVista[]>('/boveda/gastos-anteriores', { query: rango }) });
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-foreground-muted">Gastos registrados antes de la bóveda; no afectan los saldos.</p>
      <div className="flex flex-wrap items-end gap-2">
        <Campo etiqueta="Desde">{(p) => <Input {...p} type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} />}</Campo>
        <Campo etiqueta="Hasta">{(p) => <Input {...p} type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} />}</Campo>
      </div>
      <Tarjeta>
        {lista.isLoading ? (
          <Esqueleto className="m-4 h-32" />
        ) : !lista.data?.length ? (
          <Vacio icono={<ReceiptText />} titulo="Sin gastos en el periodo" />
        ) : (
          <div className="overflow-x-auto">
            <Tabla>
              <thead>
                <tr>
                  <Th>Fecha</Th>
                  <Th>Categoría</Th>
                  <Th>Descripción</Th>
                  <Th className="text-right">Monto</Th>
                </tr>
              </thead>
              <tbody>
                {lista.data.map((g) => (
                  <Tr key={g.id}>
                    <Td className="cifras whitespace-nowrap">{fecha(`${g.fecha}T12:00:00-06:00`)}</Td>
                    <Td>{ETIQUETA_CATEGORIA[g.categoria]}</Td>
                    <Td>{g.descripcion}</Td>
                    <Td className="cifras text-right">{formatearMoneda(g.monto, g.moneda)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Tabla>
          </div>
        )}
      </Tarjeta>
    </div>
  );
}

/** Caja fuerte (efectivo) y cuenta de banco: saldos, movimientos y gastos que salen de ellas. */
export function BovedaPage() {
  const { puede } = useSesion();
  const saldos = useSaldos();
  const [accion, setAccion] = useState<Accion | null>(null);
  const s = saldos.data;
  const pendienteUSD = s?.pendientesSaldoInicial.some((p) => p.boveda === 'efectivo' && p.moneda === 'USD');

  return (
    <ModuleLayout titulo="Caja" secciones={SECCIONES_POR_MODULO.caja}>
      <EncabezadoPagina
        titulo="Bóveda"
        descripcion="Caja fuerte y banco: lo que entra al cerrar caja y todo lo que se paga."
        acciones={
          <div className="flex flex-wrap gap-2">
            {puede('boveda.ingresar') && (
              <Button variante="secundario" onClick={() => setAccion({ tipo: 'ingresar' })}>
                <ArrowDownToLine /> Ingresar
              </Button>
            )}
            {puede('boveda.retirar') && (
              <Button variante="secundario" onClick={() => setAccion({ tipo: 'retirar' })}>
                <ArrowUpFromLine /> Retirar / pagar
              </Button>
            )}
            {puede('boveda.traspasar') && (
              <Button variante="secundario" onClick={() => setAccion({ tipo: 'traspasar' })}>
                <ArrowLeftRight /> Traspasar
              </Button>
            )}
          </div>
        }
      />
      {!s ? (
        <Esqueleto className="mb-4 h-24" />
      ) : (
        <>
          {s.pendientesSaldoInicial.length > 0 && puede('boveda.ingresar') && (
            <div role="alert" className="mb-4 flex flex-col gap-2 rounded-lg bg-warning-soft px-4 py-3 text-[13.5px] text-warning sm:flex-row sm:items-center">
              <span className="font-medium">Captura el saldo inicial: hasta entonces no se pueden pagar compras ni nómina desde esa bóveda.</span>
              <div className="flex flex-wrap gap-2 sm:ml-auto">
                {s.pendientesSaldoInicial.map((p) => (
                  <Button key={`${p.boveda}-${p.moneda}`} tamano="sm" variante="secundario" onClick={() => setAccion({ tipo: 'inicial', ...p })}>
                    {`Capturar saldo inicial de ${p.boveda} (${p.moneda})`}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <div className="mb-5 flex flex-col gap-3 sm:flex-row">
            <TarjetaSaldo titulo="Efectivo">
              <p className="cifras font-display text-[26px] leading-tight">{formatearMoneda(s.efectivo.MXN)}</p>
              {(s.efectivo.USD !== 0 || pendienteUSD) && <p className="cifras text-[15px] text-foreground-muted">{formatearMoneda(s.efectivo.USD, 'USD')}</p>}
            </TarjetaSaldo>
            <TarjetaSaldo titulo="Banco">
              <p className="cifras font-display text-[26px] leading-tight">{formatearMoneda(s.banco.MXN)}</p>
            </TarjetaSaldo>
          </div>
        </>
      )}
      <Pestanas defaultValue="movimientos">
        <PestanasLista className="mb-3">
          <Pestana value="movimientos">Movimientos</Pestana>
          <Pestana value="anteriores">Gastos anteriores</Pestana>
        </PestanasLista>
        <PestanaContenido value="movimientos">
          <Movimientos />
        </PestanaContenido>
        <PestanaContenido value="anteriores">
          <GastosAnteriores />
        </PestanaContenido>
      </Pestanas>
      {accion && <DialogoBoveda accion={accion} onCerrar={() => setAccion(null)} />}
    </ModuleLayout>
  );
}
